import { PgliteDatabase } from "drizzle-orm/pglite";
import { contacts, customers, InsertInvoice, invoices, SelectContact, SelectCustomer, SelectInvoice, SelectTransaction, transactions } from "../schema";
import { eq, inArray, ne, count } from "drizzle-orm";
import { Contact, InvoicesHistoryData, Statuses } from "@/types";


class InvoicesRepository {
    public add = async (db: PgliteDatabase, data: InsertInvoice): Promise<SelectInvoice> => {
        const [res] = await db.insert(invoices).values(data).returning();
        return res;
    }
    public update = async (db: PgliteDatabase, newData: InsertInvoice): Promise<SelectInvoice> => {
        const [res] = await db.update(invoices).set(newData)
            .where(eq(invoices.invoiceNumber, newData.invoiceNumber
            )).returning();
        return res;
    }

    public getAll = async (db: PgliteDatabase): Promise<SelectInvoice[]> => {
        return await db.select().from(invoices);
    }

    public getById = async (db: PgliteDatabase, invoiceNumber: string): Promise<InvoicesHistoryData> => {
        const data = await db.select().from(invoices)
            .where(eq(invoices.invoiceNumber, invoiceNumber))
            .leftJoin(customers, eq(customers.id, invoices.customerId))
            .leftJoin(transactions, eq(transactions.invoiceNumber, invoices.invoiceNumber))
            .leftJoin(contacts, eq(contacts.customerId, customers.id))
        return this.mapResult(data)[0];
    }

    public getAllUnArchived = async (db: PgliteDatabase): Promise<InvoicesHistoryData[]> => {
        const data = await db.select().from(invoices)
            .leftJoin(customers, eq(customers.id, invoices.customerId))
            .leftJoin(transactions, eq(transactions.invoiceNumber, invoices.invoiceNumber))
            .leftJoin(contacts, eq(customers.id, contacts.customerId))
            .where(ne(invoices.isArchived, true))
        return this.mapResult(data);
    }
    public getInvoicesCount = async (db: PgliteDatabase): Promise<number> => {
        const [res] = await db.select({ count: count() }).from(invoices);
        return res.count;
    }
    public getAllArchived = async (db: PgliteDatabase): Promise<InvoicesHistoryData[]> => {
        const data = await db.select().from(invoices)
            .leftJoin(customers, eq(customers.id, invoices.customerId))
            .leftJoin(transactions, eq(transactions.invoiceNumber, invoices.invoiceNumber))
            .leftJoin(contacts, eq(customers.id, contacts.customerId))
            .where(eq(invoices.isArchived, true));
        return this.mapResult(data);
    }

    public updateState = async (db: PgliteDatabase, invoiceNumber: string) => {
        const invoiceData = await db.select().from(invoices)
            .innerJoin(transactions, eq(transactions.invoiceNumber, invoiceNumber))
            .where(eq(invoices.invoiceNumber, invoiceNumber));

        const status = this.getStatus(invoiceData);

        const data = await db.update(invoices)
            .set({ status })
            .where(eq(invoices.invoiceNumber, invoiceNumber)).returning({ status: invoices.status });
        const { status: result } = data[0];
        return result;
    }

    public archive = async (db: PgliteDatabase, invoiceNumber: string) => {
        await db.update(invoices).set({
            isArchived: true,
        }).where(eq(invoices.invoiceNumber, invoiceNumber));
    }

    public delete = async (db: PgliteDatabase, invoiceNumber: string) => {
        await db.delete(invoices).where(eq(invoices.invoiceNumber, invoiceNumber));
    }
    public archiveMany = async (db: PgliteDatabase, ids: string[]) => {
        return await db.update(invoices).set({
            isArchived: true,
        }).where(inArray(invoices.invoiceNumber, ids)).returning();
    }
    public restore = async (db: PgliteDatabase, invoiceNumber: string) => {
        await db.update(invoices).set({
            isArchived: false
        }).where(eq(invoices.invoiceNumber, invoiceNumber));
    }
    public restoreMany = async (db: PgliteDatabase, ids: string[]) => {
        await db.update(invoices).set({
            isArchived: false
        }).where(inArray(invoices.invoiceNumber, ids));
    }
    private mapResult = (data: {
        invoices: SelectInvoice,
        customers: SelectCustomer | null,
        transactions: SelectTransaction | null,
        contacts: SelectContact | null
    }[]) => {
        const result = data.reduce<Record<string, InvoicesHistoryData>>((acc, item) => {
            const { invoices: invoice, transactions: transaction, customers, contacts } = item;
            const { invoiceNumber } = invoice;

            const existsItem = acc[invoiceNumber];
            if (existsItem && transaction) existsItem.transactions.push(transaction);
            else if (customers) {
                const res = {
                    ...invoice.invoiceData,
                    status: invoice.status,
                    isArchived: invoice.isArchived,
                    invoiceData: invoice.invoiceData,
                    transactions: transaction ? [transaction] : [],
                    customerInfo: customers,
                    billToCountry: customers.country?.name as string,
                    billToCountryCode: customers.country?.code as string,
                    billToAddress: customers.address,
                    billToContact: contacts as Contact,
                    lastModified: invoice.lastModified.getTime(),
                    billTo: customers.name,
                }
                acc[invoiceNumber] = res;
            }
            return acc;
        }, {});
        return Object.values(result).sort((a, b) => b.lastModified - a.lastModified);
    }
    private getStatus = (data: {
        invoices: SelectInvoice,
        transactions: SelectTransaction,
    }[]) => {
        let status: Statuses = "unpaid";

        if (data.length === 0) {
            return status;
        };

        const sum = data.reduce((acc, { transactions }) => acc + transactions.amount, 0);
        const { totalToPay } = data[0].invoices.invoiceData;
        const dif = totalToPay - sum;

        if (dif > 0 && dif < totalToPay) status = "partial-paid";
        if (dif === 0) status = "paid";
        if (dif < 0) status = "overpaid";

        return status;
    }
}

export const invoicesRepository = new InvoicesRepository();